// «Por apartado» del Pronóstico y presupuesto (7-oct-2026, Mabel: Finanzas.docx). Apartado › Categoría ›
// Concepto con el real del mes; todos los conceptos salen aunque estén en $0 (ella arma su presupuesto en
// Excel sobre esta lista: el Excel trae la hoja «Por apartado» con la columna de presupuesto vacía).
// Resultado = Ingresos − Egresos − Nómina − Inversiones (respuesta 9).
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { moneyEntero as money, PresupuestoEstructura } from '../../finances/api';

const COLOR: Record<string, string> = {
  ingreso: 'text-emerald-600 dark:text-emerald-400', egreso: 'text-red-600 dark:text-red-400',
  nomina: 'text-amber-600 dark:text-amber-400', inversion: 'text-sky-600 dark:text-sky-400',
};

export default function PorApartado({ e }: { e: PresupuestoEstructura }) {
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const [verCeros, setVerCeros] = useState(false);
  const toggle = (k: string) => setAbiertas((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const difiere = e.nominaCalculada != null && Math.abs(e.resultadoNominaCalculada - e.resultado) > 1;
  return (
    <div className="rounded-2xl border border-line bg-surface shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4 pb-2">
        <h3 className="font-display text-base font-bold text-content">Por apartado · categoría · concepto</h3>
        <label className="inline-flex items-center gap-1.5 text-xs text-content-3">
          <input type="checkbox" checked={verCeros} onChange={(ev) => setVerCeros(ev.target.checked)} /> Ver conceptos en $0
        </label>
      </div>
      <div className="divide-y divide-line">
        {e.secciones.map((s) => (
          <div key={s.section} className="px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold uppercase tracking-wide text-content">{s.label}</span>
              <span className={`font-display text-base font-bold tabular-nums ${COLOR[s.section] || 'text-content'}`}>{money(s.total)}</span>
            </div>
            <div className="mt-1.5 space-y-0.5">
              {s.categorias.filter((c) => verCeros || c.total !== 0).map((c) => {
                const k = `${s.section}:${c.name}`;
                const abierta = abiertas.has(k);
                const conceptos = c.conceptos.filter((x) => verCeros || x.total !== 0);
                return (
                  <div key={k}>
                    <button onClick={() => toggle(k)} className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left text-sm hover:bg-surface-2">
                      <span className="flex min-w-0 items-center gap-1 text-content-2">
                        {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<span className="truncate">{c.name}</span>
                      </span>
                      <span className="tabular-nums text-content">{money(c.total)}</span>
                    </button>
                    {abierta && (
                      <ul className="mb-1 ml-6 space-y-0.5">
                        {conceptos.map((x) => (
                          <li key={x.name} className="flex items-center justify-between text-xs text-content-3">
                            <span className="truncate">{x.name}{x.n ? ` (${x.n})` : ''}</span>
                            <span className="tabular-nums">{money(x.total)}</span>
                          </li>
                        ))}
                        {!conceptos.length && <li className="text-xs text-content-3">Sin movimientos</li>}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="rounded-b-2xl bg-surface-2/60 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-content">Resultado <span className="font-normal text-content-3">(Ingresos − Egresos − Nómina − Inversiones)</span></span>
            <span className={`font-display text-lg font-bold tabular-nums ${e.resultado >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{money(e.resultado)}</span>
          </div>
          {difiere && (
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-amber-700 dark:text-amber-300">Con la nómina calculada del mes ({money(e.nominaCalculada || 0)})</span>
              <span className="font-semibold tabular-nums text-content">{money(e.resultadoNominaCalculada)}</span>
            </div>
          )}
          {e.notas.map((n) => <p key={n} className="mt-1 text-[11px] text-content-3">{n}</p>)}
        </div>
      </div>
    </div>
  );
}
